import React, { useMemo } from "react";
import { Box, Grid, Stack, useTheme } from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import DevicesIcon from "@mui/icons-material/Devices";
import { StatCard, DonutChart, HorizontalBarChart } from "../components/charts";
import { DashboardCard } from "../components/DashboardCard";
import { programmeStats, demographicsData, genderDistribution, departmentDistribution, shiftPatternDistribution, travelTimeDistribution } from "../data";

export const ProgrammeProfile: React.FC = () => {
  const theme = useTheme();

  const statCards = [
    { metric: programmeStats.total_participants, icon: PeopleIcon },
    { metric: programmeStats.active_participants, icon: PersonIcon },
    { metric: programmeStats.avg_participation, icon: TrendingUpIcon },
    { metric: programmeStats.device_connection, icon: DevicesIcon },
  ];

  const genderData = useMemo(() => [
    { ...genderDistribution[0], color: theme.palette.info.main },
    { ...genderDistribution[1], color: theme.palette.error.light },
    { ...genderDistribution[2], color: theme.palette.warning.main },
  ], [theme]);

  const deptData = useMemo(() => departmentDistribution.map((d, i) => ({
    ...d,
    color: [theme.palette.primary.main, theme.palette.info.main, theme.palette.warning.main, theme.palette.success.main, theme.palette.error.light, theme.palette.secondary.main][i],
  })), [theme]);

  const shiftData = useMemo(() => shiftPatternDistribution.map((d, i) => ({
    ...d,
    color: [theme.palette.primary.main, theme.palette.warning.main, theme.palette.info.main, theme.palette.success.main][i],
  })), [theme]);

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Grid container spacing={3}>
          {statCards.map((stat, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
              <StatCard metric={stat.metric} icon={stat.icon} />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard title="Gender Distribution" subtitle="Breakdown of participants by gender.">
              <DonutChart data={genderData} height={280} />
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard title="Department Distribution" subtitle="Participants across departments.">
              <DonutChart data={deptData} height={280} />
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard title="Shift Pattern Distribution" subtitle="Working pattern breakdown.">
              <DonutChart data={shiftData} height={280} />
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <DashboardCard title="Age Distribution" subtitle="Participant age demographics.">
              <HorizontalBarChart data={demographicsData} height={280} color={theme.palette.primary.main} />
            </DashboardCard>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <DashboardCard title="Travel Time to Work" subtitle="Daily commute duration distribution.">
              <HorizontalBarChart data={travelTimeDistribution} height={280} color={theme.palette.info.main} />
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
